import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../../api/apiClient";
import "../../../assets/css/admin/organization/OrgBoardAdmin.css";
import "../../../assets/css/admin/AdminCommon.css";
import { getStoredAdminInfo } from "../AdminAuth";

import SearchD from "../../../assets/img/Search_D.svg";
import SearchF from "../../../assets/img/Search_F.svg";

const PAGE_SIZE = 10;

const categoryOptions = [
  { label: "전체", value: "ALL" },
  { label: "회계", value: "BUDGET" },
  { label: "행사/사업", value: "EVENT" },
  { label: "행정/운영", value: "ADMINISTRATION" },
  { label: "기타", value: "ETC" },
];

const searchOptions = [
  { label: "제목", value: "TITLE" },
  { label: "내용", value: "CONTENT" },
];

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10).replaceAll("-", ".");
  }

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}.${mm}.${dd}`;
};

const getPagedArray = (result) => {
  const data = result?.data;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.contents)) return data.contents;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.content)) return data.data.content;
  if (Array.isArray(data?.data?.contents)) return data.data.contents;
  if (Array.isArray(data?.data?.items)) return data.data.items;

  return [];
};

const getTotalPages = (result, listLength, page) => {
  const data = result?.data;

  const rawTotalPages =
    data?.totalPages ??
    data?.page?.totalPages ??
    data?.data?.totalPages ??
    data?.data?.page?.totalPages;

  if (Number.isFinite(Number(rawTotalPages)) && Number(rawTotalPages) > 0) {
    return Number(rawTotalPages);
  }

  const rawTotalElements =
    data?.totalElements ??
    data?.totalCount ??
    data?.total ??
    data?.data?.totalElements ??
    data?.data?.totalCount ??
    data?.data?.total;

  if (
    Number.isFinite(Number(rawTotalElements)) &&
    Number(rawTotalElements) > 0
  ) {
    return Math.max(1, Math.ceil(Number(rawTotalElements) / PAGE_SIZE));
  }

  return listLength >= PAGE_SIZE ? page + 2 : Math.max(1, page + 1);
};

const normalizeBoardItem = (item) => ({
  id: item.collegeFormsId ?? item.id,
  title: item.title ?? "",
  writer: item.name ?? item.writer ?? "관리자",
  date: formatDate(item.createdAt),
  viewer: item.viewer ?? 0,
});

export default function CcBoard() {
  const navigate = useNavigate();

  const { unitName: selectedUnitName, collegeId } = getStoredAdminInfo();

  const [categoryOpen, setCategoryOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("ALL");

  const [searchType, setSearchType] = useState("TITLE");
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const [appliedSearchType, setAppliedSearchType] = useState("TITLE");
  const [appliedKeyword, setAppliedKeyword] = useState("");

  const [boardList, setBoardList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const searchIcon = keyword.trim() ? SearchF : SearchD;

  const selectedCategoryLabel =
    categoryOptions.find((item) => item.value === selectedCategory)?.label ||
    "전체";

  const selectedSearchLabel =
    searchOptions.find((item) => item.value === searchType)?.label || "제목";

  const fetchBoardList = useCallback(
    async (page = 0) => {
      if (!collegeId) {
        alert("소속 단위 정보를 찾을 수 없습니다. 다시 로그인해 주세요.");
        setBoardList([]);
        setTotalPages(1);
        return;
      }

      try {
        setLoading(true);

        const params = new URLSearchParams({
          listCategory: selectedCategory,
          keyWordCategory: appliedSearchType,
          page: String(page),
          size: String(PAGE_SIZE),
        });

        const trimmedKeyword = appliedKeyword.trim();

        if (trimmedKeyword) {
          params.append("keyWord", trimmedKeyword);
        }

        const result = await apiClient(
          `/api/colleges/${collegeId}/form?${params.toString()}`
        );

        const list = getPagedArray(result).map(normalizeBoardItem);

        setBoardList(list);
        setTotalPages(getTotalPages(result, list.length, page));
      } catch (error) {
        console.error("게시판 목록 조회 오류:", error);
        alert(error.message || "게시글 목록 조회 중 오류가 발생했습니다.");
        setBoardList([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    [collegeId, selectedCategory, appliedSearchType, appliedKeyword]
  );

  useEffect(() => {
    setCategoryOpen(false);
    setSearchOpen(false);
    fetchBoardList(currentPage);
  }, [fetchBoardList, currentPage]);

  const pageNumbers = useMemo(() => {
    const maxVisible = 5;
    const safeTotalPages = Math.max(1, totalPages);

    const currentGroup = Math.floor(currentPage / maxVisible);
    const startPage = currentGroup * maxVisible;
    const endPage = Math.min(startPage + maxVisible, safeTotalPages);

    return Array.from(
      { length: endPage - startPage },
      (_, index) => startPage + index
    );
  }, [currentPage, totalPages]);

  const handleSelectCategory = (value) => {
    setSelectedCategory(value);
    setCategoryOpen(false);
    setCurrentPage(0);
  };

  const handleSelectSearchType = (value) => {
    setSearchType(value);
    setSearchOpen(false);
  };

  const handleSearch = () => {
    setCategoryOpen(false);
    setSearchOpen(false);

    setAppliedSearchType(searchType);
    setAppliedKeyword(keyword.trim());
    setCurrentPage(0);
  };

  const handlePageMove = (page) => {
    if (loading) return;

    const safePage = Math.min(Math.max(page, 0), Math.max(totalPages - 1, 0));

    setCurrentPage(safePage);
  };

  const handleAddMove = () => {
    if (!collegeId) {
      alert("소속 단위 정보를 찾을 수 없습니다. 다시 로그인해 주세요.");
      return;
    }

    navigate("write");
  };

  const handleEditMove = (id) => {
    if (!collegeId) {
      alert("소속 단위 정보를 찾을 수 없습니다. 다시 로그인해 주세요.");
      return;
    }

    navigate(`edit/${id}`);
  };

  const handleDelete = async (id) => {
    if (!collegeId) {
      alert("소속 단위 정보를 찾을 수 없습니다. 다시 로그인해 주세요.");
      return;
    }

    const confirmDelete = window.confirm("삭제하시겠습니까?");
    if (!confirmDelete) return;

    try {
      await apiClient(`/api/colleges/${collegeId}/form/${id}`, {
        method: "DELETE",
      });

      alert("삭제되었습니다.");

      if (boardList.length === 1 && currentPage > 0) {
        setCurrentPage((prev) => prev - 1);
      } else {
        fetchBoardList(currentPage);
      }
    } catch (error) {
      console.error("게시글 삭제 오류:", error);
      alert(error.message || "삭제 중 오류가 발생했습니다.");
    }
  };

  return (
    <div className="org-board-cc-shell">
      <div className="org-board-page admin-scroll-x">
        <div className="org-board-inner">
          <h2 className="org-board-title">
            {selectedUnitName || "중앙운영위원회"} 게시판 관리
          </h2>

          <div className="org-board-admin-toolbar">
            <button
              type="button"
              className="org-board-add-btn"
              onClick={handleAddMove}
              disabled={loading || !collegeId}
            >
              +
            </button>

            <div className="org-board-admin-filter">
              <div className="org-board-filter-left">
                <div className="org-board-filter-wrap">
                  <button
                    type="button"
                    className={`org-board-filter-btn ${
                      categoryOpen ? "is-open" : ""
                    }`}
                    onClick={() => {
                      setCategoryOpen((prev) => !prev);
                      setSearchOpen(false);
                    }}
                    disabled={!collegeId}
                  >
                    <span>{selectedCategoryLabel}</span>
                    <span className="org-board-filter-arrow" />
                  </button>

                  {categoryOpen && (
                    <div className="org-board-filter-menu">
                      {categoryOptions.map((item) => (
                        <button
                          key={item.value}
                          type="button"
                          onClick={() => handleSelectCategory(item.value)}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="org-board-filter-right">
                <div className="org-board-filter-wrap">
                  <button
                    type="button"
                    className={`org-board-filter-btn ${
                      searchOpen ? "is-open" : ""
                    }`}
                    onClick={() => {
                      setSearchOpen((prev) => !prev);
                      setCategoryOpen(false);
                    }}
                    disabled={!collegeId}
                  >
                    <span>{selectedSearchLabel}</span>
                    <span className="org-board-filter-arrow" />
                  </button>

                  {searchOpen && (
                    <div className="org-board-filter-menu">
                      {searchOptions.map((item) => (
                        <button
                          key={item.value}
                          type="button"
                          onClick={() => handleSelectSearchType(item.value)}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="org-board-search-box">
                  <input
                    type="text"
                    placeholder="검색어를 입력하세요."
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    disabled={!collegeId}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearch();
                      }
                    }}
                  />

                  <button
                    type="button"
                    className="org-board-search-btn"
                    onClick={handleSearch}
                    disabled={loading || !collegeId}
                    aria-label="검색"
                  >
                    <img src={searchIcon} alt="" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="org-board-table">
            <div className="org-board-table-header">
              <div>No</div>
              <div>제목</div>
              <div>작성자</div>
              <div>작성일</div>
              <div>관리</div>
            </div>

            {!collegeId ? (
              <div className="org-board-empty">
                소속 단위 정보를 찾을 수 없습니다. 다시 로그인해 주세요.
              </div>
            ) : loading ? (
              <div className="org-board-empty">불러오는 중입니다.</div>
            ) : boardList.length > 0 ? (
              boardList.map((item, index) => (
                <div className="org-board-table-row" key={item.id}>
                  <div>
                    {String(currentPage * PAGE_SIZE + index + 1).padStart(
                      3,
                      "0"
                    )}
                  </div>
                  <div>{item.title}</div>
                  <div>{item.writer}</div>
                  <div>{item.date}</div>

                  <div className="org-board-actions">
                    <button
                      type="button"
                      onClick={() => handleEditMove(item.id)}
                    >
                      수정
                    </button>

                    <button
                      type="button"
                      onClick={() => handleDelete(item.id)}
                    >
                      삭제
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="org-board-empty">등록된 게시글이 없습니다.</div>
            )}
          </div>

          <div className="org-board-pagination">
            <button
              type="button"
              onClick={() => handlePageMove(0)}
              disabled={loading || currentPage === 0}
            >
              ‹
            </button>

            <button
              type="button"
              onClick={() => handlePageMove(currentPage - 1)}
              disabled={loading || currentPage === 0}
            >
              ‹
            </button>

            {pageNumbers.map((page) => (
              <button
                key={page}
                type="button"
                className={currentPage === page ? "active" : ""}
                onClick={() => handlePageMove(page)}
                disabled={loading}
              >
                {page + 1}
              </button>
            ))}

            <button
              type="button"
              onClick={() => handlePageMove(currentPage + 1)}
              disabled={loading || currentPage >= totalPages - 1}
            >
              ›
            </button>

            <button
              type="button"
              onClick={() => handlePageMove(totalPages - 1)}
              disabled={loading || currentPage >= totalPages - 1}
            >
              ›
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}